import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./AuthForm.css";

const cantons = [
  "",
  "AG", "AI", "AR", "BE", "BL", "BS", "FR", "GE", "GL", "GR",
  "JU", "LU", "NE", "NW", "OW", "SG", "SH", "SZ", "TG",
  "TI", "UR", "VD", "VS", "ZG", "ZH"
];

// Mapping canton → langue
const cantonToLanguage = {
  // Français
  VD: 'fr', GE: 'fr', NE: 'fr', JU: 'fr', FR: 'fr', VS: 'fr',
  // Italien
  TI: 'it',
  // Tous les autres → allemand
};

const getLanguageForCanton = (canton) => {
  if (!canton) return null;
  return cantonToLanguage[canton] || 'de';
};

const AuthForm = ({ onSuccess }) => {
  const { t, i18n } = useTranslation();
  const [isLoginMode, setIsLoginMode] = useState(true);

  // Champs communs
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Champs d'inscription
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [canton, setCanton] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showResend, setShowResend] = useState(false); // email non confirmé → proposer de renvoyer

  const TERMS_VERSION = "2026-11-04";
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Brouillon d'inscription : évite de tout reperdre si on quitte le formulaire
  // pour aller lire les CGU / la confidentialité (mot de passe non sauvegardé).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("sda_signup_draft");
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.email != null) setEmail(d.email);
      if (d.emailConfirm != null) setEmailConfirm(d.emailConfirm);
      if (d.firstName != null) setFirstName(d.firstName);
      if (d.lastName != null) setLastName(d.lastName);
      if (d.gender != null) setGender(d.gender);
      if (d.birthDate != null) setBirthDate(d.birthDate);
      if (d.canton != null) setCanton(d.canton);
      if (d.acceptedTerms != null) setAcceptedTerms(!!d.acceptedTerms);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem("sda_signup_draft", JSON.stringify({
        email, emailConfirm, firstName, lastName, gender, birthDate, canton, acceptedTerms,
      }));
    } catch { /* noop */ }
  }, [email, emailConfirm, firstName, lastName, gender, birthDate, canton, acceptedTerms]);

  const currentLang = i18n.language?.substring(0, 2) || 'fr';

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Quand le canton change, suggérer la langue appropriée
  const handleCantonChange = (newCanton) => {
    setCanton(newCanton);
    const suggestedLang = getLanguageForCanton(newCanton);
    // Seulement si l'utilisateur n'a pas déjà manuellement changé la langue
    const hasManuallyChanged = localStorage.getItem('i18nextLngManual');
    if (suggestedLang && !hasManuallyChanged) {
      i18n.changeLanguage(suggestedLang);
    }
  };

  // Marquer quand l'utilisateur change manuellement la langue
  const handleManualLanguageChange = (lng) => {
    localStorage.setItem('i18nextLngManual', 'true');
    changeLanguage(lng);
  };

  // ---------- Handlers ----------
  const handleSignUp = async () => {
    setLoading(true);
    setMessage("");
    setMessageType("");
    if (!acceptedTerms) {
      setMessageType("error");
      setMessage(t('auth.errors.mustAcceptTerms'));
      setLoading(false);
      return;
    }
    const e1 = email.trim().toLowerCase();
    const e2 = emailConfirm.trim().toLowerCase();
    if (!e2) {
      setMessage(t('auth.errors.confirmEmail'));
      setLoading(false);
      return;
    }
    if (e1 !== e2) {
      setMessage(t('auth.errors.emailMismatch'));
      setLoading(false);
      return;
    }
    try {
      // L'inscription passe par le backend (clé service_role) : il crée le
      // compte Auth ET la ligne de profil. Le navigateur n'écrit plus jamais
      // directement dans la table `users` (compatible avec la RLS activée).
      const res = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          gender: gender || null,
          birth_date: birthDate || null,
          canton: canton || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('auth.errors.signupFailed'));

      setMessageType("success");
      setMessage(t('auth.success.signupSuccess'));
      setIsLoginMode(true);
      try { sessionStorage.removeItem("sda_signup_draft"); } catch { /* noop */ }
    } catch (error) {
      console.error("Erreur lors de l'inscription :", error.message);
      setMessageType("error");
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setMessage("");
    setMessageType("");
    setShowResend(false);
    try {
      const { data: session, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      onSuccess(session);
    } catch (error) {
      console.error("Erreur lors de la connexion :", error.message);
      setMessageType("error");
      const msg = error?.message || "";
      if (/not confirmed|confirm/i.test(msg)) {
        // Email pas encore confirmé → message clair + proposer de renvoyer le lien
        setMessage(t('auth.errors.emailNotConfirmed'));
        setShowResend(true);
      } else if (msg === "Invalid login credentials") {
        setMessage(t('auth.errors.invalidCredentials'));
      } else {
        setMessage(t('auth.errors.connectionFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Renvoyer l'email de confirmation d'inscription
  const handleResendConfirmation = async () => {
    if (!email) {
      setMessageType("error");
      setMessage(t('auth.errors.enterEmailForReset'));
      return;
    }
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) {
      setMessageType("error");
      setMessage(`Erreur : ${error.message}`);
    } else {
      setMessageType("success");
      setMessage(t('auth.success.confirmationResent'));
      setShowResend(false);
    }
  };

  const handleForgotPassword = async () => {
    setMessage("");
    setMessageType("");
    if (!email) {
      setMessage(t('auth.errors.enterEmailForReset'));
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setMessageType("error");
      const m = error.message || "";
      if (/only request this after|rate limit|too many/i.test(m)) {
        setMessage(t('auth.errors.rateLimit'));
      } else {
        setMessage(`${t('common.error')} : ${m}`);
      }
    } else {
      setMessageType("success");
      setMessage(t('auth.success.resetEmailSent'));
    }
  };

  // ---------- Rendu ----------
  return (
    <div className="auth-form-container">
      {/* Bulles animées en arrière-plan */}
      <div className="bubbles-container">
        <div className="bubble"></div>
        <div className="bubble"></div>
        <div className="bubble"></div>
        <div className="bubble"></div>
        <div className="bubble"></div>
        <div className="bubble"></div>
        <div className="bubble"></div>
        <div className="bubble"></div>
      </div>

      {/* Sélecteur de langue visible */}
      <div className="auth-language-selector">
        <button
          type="button"
          className={currentLang === 'fr' ? 'active' : ''}
          onClick={() => handleManualLanguageChange('fr')}
        >
          FR
        </button>
        <button
          type="button"
          className={currentLang === 'de' ? 'active' : ''}
          onClick={() => handleManualLanguageChange('de')}
        >
          DE
        </button>
        <button
          type="button"
          className={currentLang === 'it' ? 'active' : ''}
          onClick={() => handleManualLanguageChange('it')}
        >
          IT
        </button>
        <button
          type="button"
          className={currentLang === 'en' ? 'active' : ''}
          onClick={() => handleManualLanguageChange('en')}
        >
          EN
        </button>
      </div>

      {message && <p className={`auth-message ${messageType}`}>{message}</p>}
      {showResend && (
        <p style={{ textAlign: "center", marginTop: -6 }}>
          <button
            type="button"
            onClick={handleResendConfirmation}
            style={{ background: "none", border: "none", color: "var(--primary-blue)", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: 14 }}
          >
            {t('auth.buttons.resendConfirmation')}
          </button>
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          isLoginMode ? handleLogin() : handleSignUp();
        }}
        className="auth-form"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={isLoginMode ? "login" : "signup"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <h2 style={{ textAlign: "center", marginBottom: 15 }}>
              {isLoginMode ? t('auth.title.login') : t('auth.title.signup')}
            </h2>

            {/* Email & Mot de passe */}
            <div className="form-group">
              <label>{t('auth.labels.email')} <span className="required">*</span></label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {!isLoginMode && (
              <div className="form-group">
                <label>{t('auth.labels.confirmEmail')} <span className="required">*</span></label>
                <input
                  type="email"
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  required
                />
                {emailConfirm &&
                  email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase() && (
                    <div className="field-error">{t('auth.errors.emailMismatch')}</div>
                  )}
              </div>
            )}

            <div className="form-group">
              <label>{t('auth.labels.password')} <span className="required">*</span></label>

              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete={isLoginMode ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.passwordToggle.hide') : t('auth.passwordToggle.show')}
                  title={showPassword ? t('auth.passwordToggle.hideShort') : t('auth.passwordToggle.showShort')}
                >
                  {showPassword ? (
                    // eye-off
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2.1 3.5 3.5 2.1 21.9 20.5 20.5 21.9l-2.2-2.2A11.5 11.5 0 0 1 12 21C6 21 2 16 1 12c.6-1.7 1.5-3.2 2.7-4.5L2.1 3.5Zm6 6 1.6 1.6a2.7 2.7 0 0 0 3.8 3.8l1.6 1.6A5 5 0 0 1 8.1 9.5ZM12 7c6 0 10 5 11 5-0.4 1.1-1 2.2-1.8 3.2l-2-2A5 5 0 0 0 12 7Zm0 0" />
                    </svg>
                  ) : (
                    // eye
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 5c6 0 10 5 11 7-1 2-5 7-11 7S2 14 1 12c1-2 5-7 11-7Zm0 2C7.5 7 4.2 10.4 3.1 12 4.2 13.6 7.5 17 12 17s7.8-3.4 8.9-5C19.8 10.4 16.5 7 12 7Zm0 2.5A2.5 2.5 0 1 1 9.5 12 2.5 2.5 0 0 1 12 9.5Z" />
                    </svg>
                  )}
                </button>

              </div>
            </div>

            {isLoginMode && (
              <p style={{ textAlign: "right", marginTop: "-8px" }}>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--primary-blue)",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "0.9em"
                  }}
                >
                  {t('auth.buttons.forgotPassword')}
                </button>
              </p>
            )}

            {/* Formulaire d'inscription */}
            {!isLoginMode && (
              <>
                <div className="form-group">
                  <label>{t('auth.labels.firstName')} <span className="required">*</span></label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.labels.lastName')} <span className="required">*</span></label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.labels.gender')}</label>
                  <select value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value="">{t('common.choose')}</option>
                    <option value="H">{t('genders.male')}</option>
                    <option value="F">{t('genders.female')}</option>
                    <option value="Non-binaire">{t('genders.nonBinary')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('auth.labels.birthDate')}</label>
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>{t('auth.labels.canton')}</label>
                  <select value={canton} onChange={(e) => handleCantonChange(e.target.value)}>
                    <option value="">{t('common.choose')}</option>
                    {cantons.filter(c => c !== "").map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* CGU : seulement en mode inscription */}
            {!isLoginMode && (
              <div className="form-group" style={{ marginTop: 10 }}>
                <div className="terms-row">
                  <input
                    id="acceptedTerms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />

                  <label htmlFor="acceptedTerms" className="terms-text">
                    {t('auth.terms.acceptTerms')}{" "}
                    <a
                      href="/cgu"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="terms-link"
                    >
                      {t('auth.terms.termsLink')}
                    </a>{" "}
                    <span className="terms-version">({t('auth.terms.version')} {TERMS_VERSION})</span>.
                    <div className="terms-hint">
                      {t('auth.terms.hint')}
                    </div>
                    <div className="terms-hint">
                      <a
                        href="/confidentialite"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="terms-link"
                      >
                        {t('privacy.title')}
                      </a>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (!isLoginMode && !acceptedTerms)}
              title={!isLoginMode && !acceptedTerms ? t('auth.errors.mustAcceptTerms') : undefined}
            >
              {loading ? t('common.loading') : isLoginMode ? t('auth.buttons.login') : t('auth.buttons.signup')}
            </button>

            <p style={{ marginTop: 15, textAlign: "center" }}>
              {isLoginMode ? (
                <>
                  {t('auth.links.noAccount')}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(false);
                      setEmailConfirm("");
                      setAcceptedTerms(false);
                    }}
                    className="auth-switch"
                  >
                    {t('auth.links.signupHere')}
                  </button>
                </>
              ) : (
                <>
                  {t('auth.links.alreadyRegistered')}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoginMode(true);
                      setEmailConfirm("");
                      setAcceptedTerms(false);
                    }}

                    className="auth-switch"
                  >
                    {t('auth.links.loginHere')}
                  </button>
                </>
              )}
            </p>
          </motion.div>
        </AnimatePresence>
      </form>
    </div>
  );
};

export default AuthForm;
